import { trackCalculatorComplete } from "@/components/analytics/conversions";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { type GLCategory, type GLSex, calculateIPFGL } from "@/utils/ipf-gl";
import { seo } from "@/utils/seo";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

export const Route = createFileRoute("/_app/essay/ipf-gl-calculator")({
  component: IPFGLCalculator,
  head: () => ({
    meta: seo({
      title: "IPF GL Points Hesaplayıcı | AtalayTheCoach",
      description:
        "Classic ve Equipped powerlifting toplamınızı resmi IPF GL katsayılarıyla karşılaştırın.",
      canonical: "https://atalaythecoach.com/essay/ipf-gl-calculator",
    }),
  }),
});
function IPFGLCalculator() {
  const [sex, setSex] = useState<GLSex>("male");
  const [category, setCategory] = useState<GLCategory>("classic");
  const [bodyweight, setBodyweight] = useState("");
  const [total, setTotal] = useState("");
  const [result, setResult] = useState<ReturnType<
    typeof calculateIPFGL
  > | null>(null);
  const [error, setError] = useState("");
  const reset = () => {
    setResult(null);
    setError("");
  };
  return (
    <main className="min-h-screen bg-background pt-32 pb-16">
      <div className="container mx-auto max-w-4xl space-y-8 px-4">
        <header className="space-y-3 text-center">
          <h1 className="font-bold text-4xl">IPF GL Points Hesaplayıcı</h1>
          <p className="text-muted-foreground">
            Squat, bench press ve deadlift toplamınızı vücut ağırlığına göre
            karşılaştırın.
          </p>
          <Link
            to="/essay/wilks-calculator"
            className="text-primary underline underline-offset-4"
          >
            Wilks hesaplayıcısına geç
          </Link>
        </header>
        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Performans bilgileri</CardTitle>
            </CardHeader>
            <CardContent>
              <form
                className="space-y-5"
                onSubmit={(event) => {
                  event.preventDefault();
                  try {
                    setResult(
                      calculateIPFGL(
                        Number(bodyweight),
                        Number(total),
                        sex,
                        category,
                      ),
                    );
                    setError("");
                    trackCalculatorComplete("ipf-gl");
                  } catch (failure) {
                    setResult(null);
                    setError(
                      failure instanceof Error
                        ? failure.message
                        : "Bilgileri kontrol edin.",
                    );
                  }
                }}
              >
                <div className="space-y-2">
                  <Label htmlFor="gl-sex">Cinsiyet</Label>
                  <Select
                    value={sex}
                    onValueChange={(value) => {
                      setSex(value as GLSex);
                      reset();
                    }}
                  >
                    <SelectTrigger id="gl-sex">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Erkek</SelectItem>
                      <SelectItem value="female">Kadın</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gl-category">Kategori</Label>
                  <Select
                    value={category}
                    onValueChange={(value) => {
                      setCategory(value as GLCategory);
                      reset();
                    }}
                  >
                    <SelectTrigger id="gl-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="classic">Classic (Raw)</SelectItem>
                      <SelectItem value="equipped">Equipped</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gl-bodyweight">Vücut ağırlığı (kg)</Label>
                  <Input
                    id="gl-bodyweight"
                    type="number"
                    min="0.01"
                    step="0.01"
                    required
                    value={bodyweight}
                    onChange={(event) => {
                      setBodyweight(event.target.value);
                      reset();
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gl-total">Üç kaldırış toplamı (kg)</Label>
                  <Input
                    id="gl-total"
                    type="number"
                    min="0.01"
                    step="0.01"
                    required
                    value={total}
                    onChange={(event) => {
                      setTotal(event.target.value);
                      reset();
                    }}
                  />
                </div>
                {error && (
                  <p role="alert" className="text-destructive text-sm">
                    {error}
                  </p>
                )}
                <Button type="submit" className="w-full">
                  IPF GL Puanını Hesapla
                </Button>
              </form>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>IPF GL sonucu</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5" aria-live="polite">
              {result ? (
                <>
                  <p className="font-bold text-5xl text-primary">
                    {result.points.toLocaleString("tr-TR", {
                      maximumFractionDigits: 3,
                    })}
                  </p>
                  <p className="text-muted-foreground text-sm">
                    Katsayı: {result.coefficient.toFixed(6)}
                  </p>
                  <meter
                    className="h-4 w-full"
                    min={0}
                    max={150}
                    value={Math.min(result.points, 150)}
                    aria-label="100 puanlık referansla karşılaştırma"
                  />
                  <p>
                    {result.points < 100
                      ? "100 puanlık referans seviyenin altında"
                      : result.points === 100
                        ? "Referans seviyede"
                        : "100 puanlık referans seviyenin üzerinde"}
                  </p>
                  <p className="text-muted-foreground text-sm">
                    100 GL puanı formülün elit sporcu referansını temsil eder.
                    Bu gösterge resmi bir başlangıç/orta/elit sınıflandırması
                    değildir.
                  </p>
                </>
              ) : (
                <p className="text-muted-foreground">
                  Bilgilerinizi girerek puanınızı hesaplayın.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
        <p className="text-muted-foreground text-sm">
          Puan = Toplam × 100 / (A − B × e^(−C × Vücut Ağırlığı)). IPF yöntemine
          göre katsayı altı ondalığa yuvarlanır.{" "}
          <a
            href="https://www.powerlifting.sport/fileadmin/ipf/data/ipf-formula/IPF_GL_Coefficients-2020.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            Resmi IPF katsayıları ve yöntem
          </a>
          .
        </p>
      </div>
    </main>
  );
}
